/**
 * The motion vocabulary, from Project Studio.
 *
 *   1. Things that move because you moved them use a spring.
 *   2. Things that appear or vanish use a duration and an ease.
 *   3. Nothing interactive exceeds about 300ms — except the two calm moves
 *      below, which carry the eye between places rather than answer a click.
 *   4. Switching is one rule everywhere. Any control that picks one of a few
 *      peers (tabs, segmented choices) slides its indicator on
 *      `SPRING_SWITCH`, and the panel it governs hands over with `SWITCH`.
 *   5. Pages hand over with `surfaceVariants`: a calm, unhurried dissolve —
 *      no sideways travel — whatever direction the move goes.
 *
 * The same three durations exist as CSS variables in `styles/tokens.css`,
 * so a CSS transition and a Framer animation on one element agree.
 */
import type { Variants } from 'framer-motion'

/** Ease-out quint: arrivals read immediately, then settle. */
export const EASE_OUT = [0.16, 1, 0.3, 1] as const
/** Ease-in quint: departures hold long enough to be seen leaving. The exit
 * of a small thing (a row, a toast) uses it; a page handover does not — its
 * departure must begin the moment the move begins, so it takes ease-out. */
export const EASE_IN = [0.7, 0, 0.84, 0] as const

/** The calm curve: an even, unhurried glide — eases in gently, settles softly, never snaps. */
export const EASE_CALM = [0.25, 0.1, 0.25, 1] as const

export const DURATION = {
  fast: 0.12,
  base: 0.18,
  slow: 0.26,
} as const

/** Ambient loops: work in progress, made visible. Not transitions. */
export const LOOP = {
  spin: { duration: 0.9, ease: 'linear', repeat: Infinity },
  pulse: { duration: 1.2, ease: 'easeInOut', repeat: Infinity },
  sweep: { duration: 1.7, ease: 'easeInOut', repeat: Infinity },
} as const

export const SPRING_PANEL = { type: 'spring', stiffness: 320, damping: 34, mass: 0.9 } as const
export const SPRING_SNAPPY = { type: 'spring', stiffness: 460, damping: 36, mass: 0.6 } as const
export const SPRING_SURFACE = { type: 'spring', stiffness: 380, damping: 30, mass: 0.8 } as const
/** The switch indicator: a soft glide with no overshoot, the same for every peer selector. */
export const SPRING_SWITCH = { type: 'spring', stiffness: 260, damping: 32, mass: 0.9 } as const

/**
 * A switched panel handing over: the old one dissolves quickly and the new
 * one rises a breath and comes into focus. Pair it with
 * `<AnimatePresence mode="wait">` so two panels never show at once.
 */
export const SWITCH = {
  initial: { opacity: 0, y: 6, filter: 'blur(4px)' },
  animate: { opacity: 1, y: 0, filter: 'blur(0px)', transition: { duration: 0.42, ease: EASE_CALM } },
  exit: { opacity: 0, y: -2, filter: 'blur(2px)', transition: { duration: 0.2, ease: EASE_CALM } },
} as const

export const FADE = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: DURATION.base, ease: EASE_OUT } },
  exit: { opacity: 0, transition: { duration: DURATION.fast, ease: EASE_IN } },
} as const

/** A row arriving in a list: a fade with a breath of rise. */
export const ENTER = {
  initial: { opacity: 0, y: 6 },
  animate: { opacity: 1, y: 0, transition: { duration: DURATION.slow, ease: EASE_OUT } },
  exit: { opacity: 0, y: -4, transition: { duration: DURATION.fast, ease: EASE_IN } },
} as const

/**
 * A handover between pages, the same for every move. The leaving page
 * dissolves quickly where it stands, so two pages never muddy into each
 * other; the arriving page waits that beat, then comes into focus — a
 * whisper of rise and scale, its blur clearing — over a long, soft settle.
 * No sideways travel: a page change should feel like the room changing,
 * not a card being swiped. The `zIndex` puts the arriving page on top the
 * instant the move begins and is never animated.
 */
const HANDOVER_EXIT = { duration: 0.28, ease: EASE_CALM } as const
const HANDOVER_ARRIVE = { duration: 0.7, ease: EASE_CALM, delay: 0.12 } as const

export const surfaceVariants: Variants = {
  initial: { opacity: 0, y: 10, scale: 0.985, filter: 'blur(6px)', zIndex: 2 },
  animate: { opacity: 1, y: 0, scale: 1, filter: 'blur(0px)', zIndex: 2, transition: HANDOVER_ARRIVE },
  exit: { opacity: 0, y: 0, scale: 1, filter: 'blur(3px)', zIndex: 1, transition: HANDOVER_EXIT },
}

export const COLLAPSE = {
  initial: { height: 0, opacity: 0 },
  animate: { height: 'auto', opacity: 1, transition: { duration: DURATION.base, ease: EASE_OUT } },
  exit: { height: 0, opacity: 0, transition: { duration: DURATION.fast, ease: EASE_IN } },
} as const

/** Capped, so the last row of a long list never arrives late enough to read as lag. */
export function staggerDelay(index: number): number {
  return Math.min(index * 0.025, 0.2)
}

/**
 * The startup splash — the one place motion is allowed to be slow.
 *
 * The ~300ms ceiling governs interaction, where every millisecond is latency
 * paid twice. The splash is not interaction: it plays once, before anything
 * can be clicked, and its job is the brand arriving rather than getting out
 * of the way. The mark assembles on its own clock, holds a beat, and leaves
 * on a fade; the minimum is what that entrance plus the beat costs. The most
 * the splash will hold past the minimum is a start that something is wrong
 * with, and the app comes up to say so.
 */
export const SPLASH = {
  /** Minimum time the splash is on screen, entrance included. */
  minDurationMs: 1800,
  /** The most the splash will wait for the first server answer beyond its minimum. */
  maxHoldMs: 9000,
  /** The mark, leaving before the surface — the exit has an order, not a cut. */
  markExitDuration: 0.34,
  /** The surface following the mark out. */
  exitDuration: 0.62,
} as const
